import { useState, useEffect } from 'react';
import { ProductCard } from './ProductCard';
import type { LocalProduct } from '../../../lib/db/schema';
import { useOutletAuth } from '../../../lib/auth/OutletAuthProvider';
import { createPhotoBoothAPI } from '@photobooth/api-client';
import { getEffectiveApiBaseUrl } from '../../../lib/config/terminal-urls';
import type { Product, OutletPricing } from '@photobooth/types';

interface ProductListProps {
  onProductSelect: (product: LocalProduct) => void;
}

const apiClient = createPhotoBoothAPI({
  baseUrl: getEffectiveApiBaseUrl(),
});

function mapApiProductToLocal(product: Product & { pricing?: OutletPricing }): LocalProduct {
  const pricing = product.pricing;

  const basePrice = product.basePrice;

  let originalPrice = basePrice;
  let effectivePrice = basePrice;
  let hasOutletPricing = false;

  if (pricing) {
    hasOutletPricing = true;
    originalPrice = pricing.originalPrice ?? basePrice;

    if (pricing.isPromotionActive && pricing.discountedPrice != null) {
      effectivePrice = pricing.discountedPrice;
    } else {
      effectivePrice = originalPrice;
    }
  }

  return {
    productId: product.productId,
    categoryId: product.categoryId,
    name: product.name,
    description: product.description ?? '',
    shotCount: product.shotCount,
    printCount: product.printCount,
    paperType: product.paperType,
    basePrice,
    price: effectivePrice,
    originalPrice,
    hasOutletPricing,
    isActive: product.isActive,
    syncedAt: Date.now(),
    productType: product.productType ?? 'session',
    countdownSeconds: product.countdownSeconds ?? undefined,
    reviewPhotoSeconds: product.reviewPhotoSeconds ?? undefined,
    editCountdownSeconds: product.editCountdownSeconds ?? undefined,
  };
}

export function ProductList({ onProductSelect }: ProductListProps) {
  const { outlet } = useOutletAuth();
  const [products, setProducts] = useState<LocalProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadProducts();
  }, [outlet?.outletId]);

  async function loadProducts() {
    try {
      setLoading(true);
      setError(null);
      let loadedProducts: LocalProduct[] = [];

      // Try to load products from API for the current outlet
      if (outlet?.outletId) {
        try {
          const response = await apiClient.products.getByOutlet(outlet.outletId);

          if (response.success && response.data) {
            let apiProducts = response.data as Array<Product & { pricing?: OutletPricing }>;

            // If outlet has an explicit product allow-list, filter to those only
            if (Array.isArray(outlet.productIds) && outlet.productIds.length > 0) {
              const allowedIds = new Set(outlet.productIds);
              apiProducts = apiProducts.filter((p) => allowedIds.has(p.productId));
            }

            loadedProducts = apiProducts
              .map((p) => mapApiProductToLocal(p))
              .filter(
                (p) =>
                  p.isActive &&
                  (p.productType === 'session' || !p.productType)
              );
          } else if (response.error) {
            console.warn('Failed to load products from API:', response.error);
          }
        } catch (apiError) {
          console.error('Error loading products from API:', apiError);
        }
      }

      setProducts(loadedProducts);
    } catch (err) {
      console.error('Failed to load products:', err);
      setError('Failed to load products. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="text-6xl mb-4 animate-bounce">📦</div>
          <p className="text-xl text-gray-600">Loading products...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <p className="text-xl text-red-600 mb-4">{error}</p>
          <button
            onClick={loadProducts}
            className="px-6 py-3 rounded-2xl bg-gradient-to-r from-pink-500 to-purple-500 text-white font-semibold hover:shadow-lg transition-all"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="text-6xl mb-4">📭</div>
          <p className="text-xl text-gray-600">No products available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto overflow-y-hidden pb-4 -mx-2 px-2 scroll-smooth touch-pan-x min-h-[560px]">
      <div
        className="grid gap-6 min-w-min"
        style={{ gridAutoFlow: 'column', gridAutoColumns: '320px', gridTemplateRows: '460px' }}
      >
        {products.map((product) => (
          <div key={product.productId} className="min-h-0 h-full">
            <ProductCard
              product={product}
              onSelect={onProductSelect}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
